/**
 * Sign-in page — BetterAuth backed.
 *
 * States: idle (email+password) → twoFactorRequired (TOTP code or backup
 * code) → success. Animated transitions between states via motion v12.
 */
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AnimatePresence, motion } from 'motion/react';
import { toast } from 'sonner';
import { authClient } from '@/shared/auth/authClient';
import { useAuth } from '@/shared/auth/AuthProvider';
import { useSignupMode } from '@/shared/api/hooks';
import { Button, Form, FormTextField } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';
import { use2FAChallenge } from './use2FAChallenge';
import { PinCodeInput } from './PinCodeInput';

// ─── Schemas ────────────────────────────────────────────────────────────────

const signInSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type SignInValues = z.infer<typeof signInSchema>;

// BetterAuth's default backup-code generator emits 5-char-dash-5-char codes
// (e.g. `hM7xA-xBDCW`, 11 chars). The verify endpoint compares via exact
// string match (plugins/two-factor/backup-codes/index.mjs:40), so the dash
// MUST be preserved when submitting. We only trim whitespace from paste
// artifacts. Loose ≥8 length keeps things working if the generator config
// is ever tweaked to a different code length.
const recoveryCodeSchema = z.object({
  code: z
    .string()
    .min(1, 'Enter your recovery code')
    .refine((v) => v.trim().length >= 8, 'That code looks too short'),
});
type RecoveryCodeValues = z.infer<typeof recoveryCodeSchema>;

// Generic error — we deliberately don't reveal whether the email exists,
// whether the email is unverified, etc. (Phase 2 finding 10.11 / priority
// #15: kill email enumeration via auth error responses).
const GENERIC_LOGIN_ERROR = 'Invalid email or password.';

// ─── Component ──────────────────────────────────────────────────────────────

export default function LoginPage() {
  const { isAuthed, loading } = useAuth();
  const { data: signupMode } = useSignupMode();
  const navigate = useNavigate();
  const location = useLocation();
  const intendedPath = location.state?.from?.pathname ?? '/';
  // Also support ?redirect= query param for the AcceptInvitationPage
  // → /login?redirect=/accept-invitation?code=... bounce.
  const searchParams = new URLSearchParams(location.search);
  const rawRedirect = searchParams.get('redirect') ?? intendedPath;
  // Open-redirect defence: only honour same-origin paths. Reject
  // absolute URLs (https://evil.com/...), protocol-relative URLs
  // (//evil.com/...), and anything that doesn't start with a single
  // forward slash. Falls back to the dashboard.
  const redirectTo =
    rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/';

  const challenge = use2FAChallenge();
  // 2FA challenge state — owned here rather than in challenge state so the
  // PIN cells stay controlled across re-renders. mode flips between TOTP
  // (six-digit code from authenticator) and backup (a single-use recovery
  // code typed manually).
  const [twoFactorMode, setTwoFactorMode] = useState<'totp' | 'backup'>('totp');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState<string | null>(null);

  const passwordForm = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  const recoveryCodeForm = useForm<RecoveryCodeValues>({
    resolver: zodResolver(recoveryCodeSchema),
    defaultValues: { code: '' },
    mode: 'onBlur',
  });

  // ─── Handlers ────────────────────────────────────────────────────────────

  const onSignIn: SubmitHandler<SignInValues> = async ({ email, password }) => {
    challenge.startSubmitting();
    const { data, error } = await authClient.signIn.email({
      email,
      password,
    });

    if (error) {
      // EMAIL_NOT_VERIFIED is the one error we surface specifically rather
      // than masking with the generic message. The "leak" of "this email
      // is registered but unverified" is mild — the signup form leaks the
      // same info ("email already in use") — and the UX cost of NOT
      // surfacing it is high (users get permanently stuck if they closed
      // the verify-email page). Matches Stripe/Linear/Vercel/GitHub.
      // Auto-resend so the user doesn't have to take a second action.
      const code = (error as { code?: string; status?: number }).code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        challenge.fail('Email not verified');
        await authClient.sendVerificationEmail({
          email,
          callbackURL: `${window.location.origin}/email-verified`,
        });
        toast.success(
          "Your email isn't verified yet. We've sent you a new verification link — check your inbox."
        );
        return;
      }
      challenge.fail(error.message ?? GENERIC_LOGIN_ERROR);
      toast.error(GENERIC_LOGIN_ERROR);
      return;
    }

    // BetterAuth returns { twoFactorRedirect: true, ... } when 2FA is
    // required. Verified at v1.6.9 plugins/two-factor/index.mjs:257.
    if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
      challenge.twoFactorRequired(['totp']);
      return;
    }

    challenge.succeed();
    navigate(redirectTo, { replace: true });
  };

  const onVerifyTotp = async () => {
    if (!/^\d{6}$/.test(totpCode)) {
      setTotpError('Enter all 6 digits from your authenticator app.');
      return;
    }
    setTotpError(null);
    challenge.startVerifyingTwoFactor();
    const { data, error } = await authClient.twoFactor.verifyTotp({ code: totpCode });

    if (error) {
      challenge.fail(error.message ?? 'Invalid code');
      setTotpError(error.message ?? "That code didn't match. Try again.");
      setTotpCode('');
      return;
    }

    if (data) {
      challenge.succeed();
      navigate(redirectTo, { replace: true });
    }
  };

  const onRecoveryCode: SubmitHandler<RecoveryCodeValues> = async ({ code }) => {
    challenge.startVerifyingTwoFactor();
    // Trim only — BetterAuth's verifyBackupCode does an EXACT string match
    // against the stored code, which is generated WITH the dash baked in
    // (e.g. `hM7xA-xBDCW`). Stripping the dash would guarantee a miss.
    const cleaned = code.trim();
    const { data, error } = await authClient.twoFactor.verifyBackupCode({ code: cleaned });

    if (error) {
      challenge.fail(error.message ?? 'Invalid recovery code');
      toast.error(error.message ?? 'Invalid recovery code');
      recoveryCodeForm.setValue('code', '');
      return;
    }

    if (data) {
      challenge.succeed();
      toast.success('Signed in with recovery code. Consider regenerating your codes in settings.');
      navigate(redirectTo, { replace: true });
    }
  };

  // ─── Redirect already-authed (after all hooks declared) ──────────────────

  if (!loading && isAuthed) {
    return <Navigate to={redirectTo} replace />;
  }

  // While the initial auth check is in flight, render an empty layout so
  // already-signed-in users don't see the login form flash before the
  // redirect kicks in (e.g. hitting /login directly while authed).
  if (loading) {
    return (
      <AuthLayout>
        <div />
      </AuthLayout>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  const inTwoFactorState =
    challenge.state.kind === 'twoFactorRequired' || challenge.state.kind === 'twoFactorVerifying';

  const submitting = challenge.state.kind === 'submitting';
  const verifying = challenge.state.kind === 'twoFactorVerifying';

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <Logomark size={48} />
        </div>
        <h1 className="text-2xl font-bold">Sign In</h1>
      </div>

      <AnimatePresence mode="wait">
        {!inTwoFactorState && (
          <motion.div
            key="password"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.2 }}
          >
            <Form onSubmit={passwordForm.handleSubmit(onSignIn)} className="space-y-4">
              <FormTextField
                control={passwordForm.control}
                name="email"
                label="Email"
                type="email"
                autoComplete="email"
                isRequired
                placeholder="you@example.com"
              />
              <FormTextField
                control={passwordForm.control}
                name="password"
                label="Password"
                type="password"
                autoComplete="current-password"
                isRequired
                placeholder="Enter your password"
              />
              <Button type="submit" isDisabled={submitting} className="w-full">
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingIcon size="sm" />
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </Button>
              {/* Sign-up CTA disappears when invite-only mode is on —
                  there's no point linking to a form that would just
                  reject the user. Forgot-password centres in that case. */}
              <div
                className={`flex text-sm pt-2 ${
                  signupMode?.inviteOnly ? 'justify-center' : 'justify-between'
                }`}
              >
                <Link
                  to="/forgot-password"
                  className="text-[var(--color-accent-text)] hover:underline"
                >
                  Forgot password?
                </Link>
                {!signupMode?.inviteOnly && (
                  <Link to="/sign-up" className="text-[var(--color-accent-text)] hover:underline">
                    Create account
                  </Link>
                )}
              </div>
            </Form>
          </motion.div>
        )}

        {inTwoFactorState && twoFactorMode === 'totp' && (
          <motion.div
            key="twofactor-totp"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-4">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Open your authenticator app and enter the 6-digit code.
              </p>
              <PinCodeInput
                label="6-digit code"
                value={totpCode}
                onChange={(v) => {
                  setTotpCode(v);
                  if (totpError) setTotpError(null);
                }}
                onComplete={() => {
                  // Auto-submit when all 6 digits are entered. The button
                  // remains as a fallback for keyboard-only flows.
                  if (!verifying) void onVerifyTotp();
                }}
                autoFocus
                isInvalid={!!totpError}
                errorMessage={totpError ?? undefined}
                isDisabled={verifying}
              />
              <Button
                type="button"
                isDisabled={verifying}
                className="w-full"
                onPress={() => void onVerifyTotp()}
              >
                {verifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingIcon size="sm" />
                    Verifying...
                  </span>
                ) : (
                  'Verify'
                )}
              </Button>
              <div className="flex flex-col gap-2 items-center text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setTwoFactorMode('backup');
                    setTotpError(null);
                    setTotpCode('');
                  }}
                  className="hover:underline"
                  style={{ color: 'var(--color-accent-text)' }}
                >
                  Use a recovery code instead
                </button>
                <button
                  type="button"
                  onClick={() => {
                    challenge.reset();
                    setTotpCode('');
                    setTotpError(null);
                  }}
                  className="hover:underline"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Back to password
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {inTwoFactorState && twoFactorMode === 'backup' && (
          <motion.div
            key="twofactor-backup"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <Form onSubmit={recoveryCodeForm.handleSubmit(onRecoveryCode)} className="space-y-4">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Lost access to your authenticator? Enter one of the backup codes you saved when 2FA
                was set up. Each code can be used once.
              </p>
              <FormTextField
                control={recoveryCodeForm.control}
                name="code"
                label="Recovery code"
                type="text"
                autoComplete="one-time-code"
                // 5-char-dash-5-char shape mirrors the generator output so
                // users know exactly how much to type. The dash matters —
                // verifyBackupCode is an exact string match.
                placeholder="xxxxx-xxxxx"
                isRequired
                autoFocus
              />
              <Button type="submit" isDisabled={verifying} className="w-full">
                {verifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingIcon size="sm" />
                    Verifying...
                  </span>
                ) : (
                  'Verify recovery code'
                )}
              </Button>
              <div className="flex flex-col gap-2 items-center text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setTwoFactorMode('totp');
                    recoveryCodeForm.reset();
                  }}
                  className="hover:underline"
                  style={{ color: 'var(--color-accent-text)' }}
                >
                  Use authenticator code instead
                </button>
                <button
                  type="button"
                  onClick={() => {
                    challenge.reset();
                    recoveryCodeForm.reset();
                    setTwoFactorMode('totp');
                  }}
                  className="hover:underline"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Back to password
                </button>
              </div>
            </Form>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
